import { DocumentRecordType, TargetSchema } from "@/types";

/**
 * Per document type: the "top-row" schema (whose tab is shown first and whose
 * rows support expansion in the review grid) and the linked schemas that nest
 * inside the expand panel.
 */
export interface EntityGrouping {
  topSchema: TargetSchema;
  nestedSchemas: TargetSchema[];
}

/**
 * Maps each supported document record type to its entity rollup shape.
 *
 * The top-row schema answers "one row represents one ___" in the review grid.
 * Nested schemas populate the expand panel for each top-row record.
 *
 * Document types not listed here fall back to flat per-schema tabs with no
 * rollup — callers should treat a missing grouping as "render schemas in the
 * order the transformation provides them."
 */
export const ENTITY_GROUPINGS: Partial<Record<DocumentRecordType, EntityGrouping>> = {
  [DocumentRecordType.STOPLOSS_CLAIMS]: {
    topSchema: TargetSchema.CLAIMANT,
    nestedSchemas: [TargetSchema.STOPLOSS_CLAIM, TargetSchema.CLAIM_ITEM],
  },
  [DocumentRecordType.POLICIES]: {
    topSchema: TargetSchema.POLICY,
    nestedSchemas: [TargetSchema.CLAIMANT],
  },
  [DocumentRecordType.REIMBURSEMENTS]: {
    topSchema: TargetSchema.CLAIMANT,
    nestedSchemas: [TargetSchema.REIMBURSEMENT],
  },
};

/**
 * Reorders available schemas so the grouping's top-row schema is first.
 * Returns the input untouched when no grouping applies or the top schema
 * isn't present in the transformation.
 */
export const reorderSchemasForGrouping = (
  schemas: TargetSchema[],
  grouping: EntityGrouping | undefined
): TargetSchema[] => {
  if (!grouping || !schemas.includes(grouping.topSchema)) return schemas;
  return [grouping.topSchema, ...schemas.filter((s) => s !== grouping.topSchema)];
};

/** Number of priority columns shown inline on the top-row schema before overflow moves to expandRow. */
export const TOP_ROW_PRIORITY_COLUMNS = 5;
